import { getBoolFromBitValue } from '../../../common/utils/helpers';
import { Logger } from '../../../common/utils/logger';
import {
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
	ATLASSIAN_SHOW_BANNER_COOKIE_KEY,
} from '../../../constants';
import type { ValidConsentData } from '../../cookie-controls/get-consent-token/types';
import { getValidConsentData } from '../../cookie-controls/get-consent-token/utils';
import { getCookie } from '../../cookie-controls/get-cookie';
import { setRequiredConsentCookies } from '../../cookie-controls/set-required-consent-cookies';
import { checkTokenVersionValidity } from '../../cookie-controls/transformer';

/**
  Asynchronously fetches data from ConsentHub (or uses cached cookies from the client) to determine banner visibility.
  Useful for: consumers who want to avoid adding the banner to their bundled code, if it should be hidden to begin with
*/
export const shouldRenderBanner = async (): Promise<boolean> => {
	const fallbackConsentToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY);
	const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY) || '';

	const cachedShouldShowBanner = getBoolFromBitValue(getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY));
	if (cachedShouldShowBanner) {
		Logger.debug(
			`shouldRenderBanner: Rendering banner becaue of show banner cookie ${ATLASSIAN_SHOW_BANNER_COOKIE_KEY}`,
		);

		/* Once consents are made in a live environment, this function will return false 98% of the time
      making it nearly impossible to test. This check gives us the capability to force showing the banner for testing.
      ... However, we are introducing a potential discrepancy issue in the future for some adopters where this
      cache bit check may be out of sync/inaccurate, but we're okay with that for now. */
		return true;
	}

	const noCachedCookies = !consentToken;
	const validCachedConsentToken = checkTokenVersionValidity(consentToken).isValid;

	const consentData: ValidConsentData | undefined = noCachedCookies
		? await getValidConsentData(fallbackConsentToken)
		: {
				consentToken,
				fallbackConsentToken,
			};

	if (!consentData || (!noCachedCookies && !validCachedConsentToken)) {
		Logger.debug('shouldRenderBanner: Rendered banner because no consent data found');
		// No consent data: the token value we fetched was deemed invalid for some reason, show the banner for compliance
		return true;
	}

	setRequiredConsentCookies(consentData.consentToken, consentData.consentTimestamp);
	const shouldShowBanner = getBoolFromBitValue(getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY));
	Logger.debug(
		`shouldShowBanner: render computed based on ATLASSIAN_SHOW_BANNER_COOKIE_KEY after setting consents: ${shouldShowBanner}`,
	);
	return !!shouldShowBanner;
};
